import type { Msg, Session } from '@halo/chat-sdk-react';
import {
  MessageMetaRoot,
  MessageBubble,
  MessageTransfer,
  TransferStatus as TransferStatusComponent,
  ItemExpireContext as MessageExpireContext,
} from '@halo/ui-chat-react';
import { Center } from '@chakra-ui/react';
import {
  isMyMessage,
  isSessionCategory,
  SessionCategoryPrefix,
  TransferStatus,
  useStaticAppStore,
} from '@/store/client';
import { useUserInfo } from '@/scripts/hooks';
import { useActionsStore } from '@/store/actions';
import { Emoji } from '@/components/Emoji';
import {
  MsgAvatar,
  MsgBody,
  MsgExpireAnimate,
  MsgFooter,
  MsgFooterEdit,
  MsgFooterReplies,
  MsgHeader,
  MsgItem,
  MsgMenuTrigger,
  MsgReadStatus,
  MsgReply,
} from '../../msg-parts';
import { getTransferBgImg } from '../../ChatContent/helpers';

export interface MsgTransferProps {
  session: Session;
  message: Msg;
}

export const MsgTransfer = memo(function MsgTransfer({ session, message }: MsgTransferProps) {
  const { sendTo } = message.payload;
  const { user: currentUser } = useStaticAppStore();
  const { user } = useUserInfo(message.owner_id);
  const { user: toUser } = useUserInfo(sendTo);
  const setTransferDetailDialogIsOpen = useActionsStore(
    (store) => store.setTransferDetailDialogIsOpen
  );
  const isMy = useMemo(() => {
    return isMyMessage(message, currentUser?.user_id);
  }, [message, currentUser?.user_id]);

  const isGroup = useMemo(() => {
    return isSessionCategory(session.chat_id, SessionCategoryPrefix.Group);
  }, [session]);

  const bgImage = useMemo(() => {
    return getTransferBgImg(message.payload?.chainType);
  }, [message.payload?.chainType]);

  const opacity = useMemo(() => {
    return message.payload?.status === TransferStatus.Failed ? 0.8 : 1;
  }, [message.payload?.status]);

  const onClickTransfer = useCallback(
    async (message: Msg) => {
      if (!user) {
        return;
      }

      setTransferDetailDialogIsOpen(true, { user, currentUser, message });
    },
    [currentUser, setTransferDetailDialogIsOpen, user]
  );

  return (
    <MsgItem message={message}>
      <MessageExpireContext message={message}>
        <MsgAvatar message={message} session={session} />
        <MsgBody message={message}>
          <MsgHeader message={message} session={session} />
          <MsgMenuTrigger message={message}>
            <MsgExpireAnimate session={session} message={message}>
              <MessageBubble
                bg="none"
                bgImage={bgImage}
                bgSize="100% 100%"
                bgPos="center"
                bgRepeat="no-repeat"
                opacity={opacity}
              >
                {message.link_msg_id && (
                  <MsgReply session={session} link_msg_id={message.link_msg_id} />
                )}
                <MessageTransfer
                  message={message}
                  toUser={toUser}
                  isGroup={isGroup}
                  isMy={isMy}
                  onClick={onClickTransfer}
                />
                <MessageMetaRoot color="white">
                  {isMy && <TransferStatusComponent status={message.payload?.status} />}
                  <Center marginLeft="auto" gap="1.5">
                    <MsgReadStatus message={message} session={session} />
                  </Center>
                </MessageMetaRoot>
              </MessageBubble>
            </MsgExpireAnimate>
          </MsgMenuTrigger>
          <MsgFooter message={message}>
            <Emoji.MessageMeta chat_id={session.chat_id} message={message} />
            <MsgFooterEdit session={session} message={message} />
            <MsgFooterReplies message={message} />
          </MsgFooter>
        </MsgBody>
      </MessageExpireContext>
    </MsgItem>
  );
});
